import { z } from 'zod';

/**
 * Server settings: the levers the team turns while the game runs — the economy, anti-abuse caps,
 * AI spend and models, payment testers — changed from the back office without a deploy or a restart.
 * Server-only: never sent to the app (that is AppConfig). Every field is optional; one left out uses
 * the API's environment default, so a fresh database behaves exactly as the env says.
 *
 * Secrets, URLs, connections and machine sizing stay in the environment.
 */
export const ServerSettings = z.object({
  /** Bumped on every save. */
  revision: z.number().int().min(0).default(0),

  // ---------- economy ----------
  /** Chapter tickets every new player gets (once per person: StarterGiftService). */
  welcomeCredits: z.number().int().min(0).max(50).optional(),
  /** 'signup': with the character; 'day3': on the first visit from the save's third game day. */
  welcomeCreditsMode: z.enum(['signup', 'day3']).optional(),
  /** Guests from one IP a day who get the starter gifts at once (the rest after signing in); 0 = no cap. */
  starterGiftsPerIpDay: z.number().int().min(0).max(100).optional(),
  /** Chapter tickets one full chapter costs. */
  illustratedCreditCost: z.number().int().min(1).max(40).optional(),
  /** The free first illustrated chapter draws only this many pages (0 = all of it). */
  firstFreeIllustratedPages: z.number().int().min(0).max(10).optional(),

  // ---------- AI ----------
  /** 'authored': no AI-written chapters (a kill switch when the model misbehaves). */
  storyMode: z.enum(['ai', 'authored']).optional(),
  /** AI spend allowed per day across all players, in US dollars; past it AI features fall back. */
  aiDailyBudgetUsd: z.number().min(0).max(10_000).optional(),
  storyReasoningEffort: z.enum(['none', 'minimal', 'low', 'medium', 'high']).optional(),
  /** Text models by purpose; a purpose left out uses `default`. */
  aiModels: z
    .object({
      default: z.string().min(1).max(80).optional(),
      intent: z.string().min(1).max(80).optional(),
      narration: z.string().min(1).max(80).optional(),
      storyboard: z.string().min(1).max(80).optional(),
      chat: z.string().min(1).max(80).optional(),
    })
    .optional(),
  imageModel: z.string().min(1).max(80).optional(),
  imageQuality: z.enum(['low', 'medium', 'high']).optional(),
  /** Quality of illustrated manga pages. */
  imagePageQuality: z.enum(['low', 'medium', 'high']).optional(),
  /** Tries per illustrated page before it counts as not drawn. */
  pageMaxAttempts: z.number().int().min(1).max(5).optional(),
  /** 'model': the image model letters the page; 'ours': we letter it. */
  pageLettering: z.enum(['model', 'ours']).optional(),

  // ---------- payments ----------
  /** Stripe test-mode purchases: 'allow' for everyone, 'testers' only for `sandboxTesters`. */
  sandboxMode: z.enum(['allow', 'testers']).optional(),
  /** User ids whose test-mode purchases apply in 'testers' mode. */
  sandboxTesters: z.array(z.string().min(1).max(64)).max(200).optional(),
});
export type ServerSettings = z.infer<typeof ServerSettings>;

/** The settings in force: every field filled, from the stored settings or the environment. */
export type EffectiveSettings = Required<Omit<ServerSettings, 'revision' | 'aiModels' | 'storyReasoningEffort'>> & {
  aiModels: Required<NonNullable<ServerSettings['aiModels']>>;
  storyReasoningEffort: NonNullable<ServerSettings['storyReasoningEffort']>;
};

/** GET /admin/settings: what is stored, what is in force, and what the environment would give. */
export interface ServerSettingsView {
  stored: ServerSettings;
  effective: EffectiveSettings;
  defaults: EffectiveSettings;
}
